// import _ from 'lodash'
import { adminClient as apolloClient } from '@/store/apollo';

import PropertyGql from '@/extensions/shop/store/graphql/queries/Property.gql';
import PropertiesGql from '@/extensions/shop/store/graphql/queries/Properties.gql';
import PropertiesPaginatedGql from '@/extensions/shop/store/graphql/queries/PropertiesPaginated.gql';

import PropertyKeysGql from '@/extensions/shop/store/graphql/queries/PropertyKeys.gql';
import ValidatePropertyKeyGql from '@/extensions/shop/store/graphql/queries/ValidatePropertyKey.gql';
import ValidatePropertyValueOptionKeyGql from '@/extensions/shop/store/graphql/queries/ValidatePropertyValueOptionKey.gql';
import PropertyValueOptionsPaginatedGql from '@/extensions/shop/store/graphql/queries/PropertyValueOptionsPaginated.gql';
import MovePropertyValueOptionGql from '@/extensions/shop/store/graphql/mutations/MovePropertyValueOption.gql';
import SortPropertyValueOptionsGql from '@/extensions/shop/store/graphql/mutations/SortPropertyValueOptions.gql';
import RestorePropertyValueOptionOrderGql from '@/extensions/shop/store/graphql/mutations/RestorePropertyValueOptionOrder.gql';
import MergePropertyValueOptionsGql from '@/extensions/shop/store/graphql/mutations/MergePropertyValueOptions.gql';
import PropertyValueOptionsGql from '@/extensions/shop/store/graphql/queries/PropertyValueOptions.gql';
import ProductPropertyPivotGql from '@/extensions/shop/store/graphql/queries/ProductPropertyPivot.gql';

import CreatePropertyGql from '@/extensions/shop/store/graphql/mutations/CreateProperty.gql';
import UpdatePropertyGql from '@/extensions/shop/store/graphql/mutations/UpdateProperty.gql';
import DeletePropertyGql from '@/extensions/shop/store/graphql/mutations/DeleteProperty.gql';
import ClonePropertyGql from '@/extensions/shop/store/graphql/mutations/CloneProperty.gql';
import ClearUnlinkedPropertyValueOptionsGql from '@/extensions/shop/store/graphql/mutations/ClearUnlinkedPropertyValueOptions.gql';
import ExportPropertyValueOptionsGql from '@/extensions/shop/store/graphql/mutations/ExportPropertyValueOptions.gql';
import ValidatePropertyValueOptionImportCsvGql from '@/extensions/shop/store/graphql/mutations/ValidatePropertyValueOptionImportCsv.gql';
import ImportPropertyValueOptionsGql from '@/extensions/shop/store/graphql/mutations/ImportPropertyValueOptions.gql';

import TranslatePropertyGql from '@/extensions/shop/store/graphql/mutations/TranslateProperty.gql';

import ProcessPropertyPaginationBulkActionGql from '@/extensions/shop/store/graphql/mutations/ProcessPropertyPaginationBulkAction.gql';

// The condition fields travel as bare references: the trigger and its options are
// edited on their own property, so the payload only has to say which ones.
const reduceConditionFields = (property) => {
    if (_.has(property, 'conditionProperty')) {
        const id = _.get(property, 'conditionProperty.id');
        property.conditionProperty = id ? { id } : null;
    }
    if (_.has(property, 'conditionValueOptions')) {
        property.conditionValueOptions = (property.conditionValueOptions || [])
            .filter((valueOption) => valueOption && valueOption.id)
            .map((valueOption) => ({ id: valueOption.id }));
    }

    return property;
};

export default {
    autocomplete: (shopID, propID, valueField, query = null, limit = null) =>
        new Promise((resolve, reject) => {
            let variables = { shop: parseInt(shopID), property: parseInt(propID), valueField };
            if (query) variables.query = query;
            if (limit) variables.limit = parseInt(limit);

            apolloClient
                .query({
                    query: PropertyValueOptionsGql,
                    variables
                })
                .then(({ data }) => resolve(data.CO_PropertyValueOptions))
                .catch(err => reject(err));
        }),
    fetchProductPropertyPivot: (id) =>
        new Promise((resolve, reject) => {
            let variables = { id: parseInt(id) };

            apolloClient
                .query({
                    query: ProductPropertyPivotGql,
                    variables
                })
                .then(({ data }) => resolve(data.CO_ProductPropertyPivot))
                .catch(err => reject(err));
        }),
    validateKey: (key, shopID = null, excludedPropertyID = null) =>
        new Promise((resolve, reject) => {
            let variables = { key };
            if (shopID) variables.shop = parseInt(shopID);
            if (excludedPropertyID) variables.excludedProperty = parseInt(excludedPropertyID);

            apolloClient
                .query({
                    query: ValidatePropertyKeyGql,
                    variables
                    // fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.CO_ValidatePropertyKey))
                .catch(err => reject(err));
        }),
    validateValueOptionKey: (key, propertyID, excludedValueOptionID = null) =>
        new Promise((resolve, reject) => {
            let variables = { key, property: parseInt(propertyID) };
            if (excludedValueOptionID) variables.excludedValueOption = parseInt(excludedValueOptionID);

            apolloClient
                .query({
                    query: ValidatePropertyValueOptionKeyGql,
                    variables
                })
                .then(({ data }) => resolve(data.CO_ValidatePropertyValueOptionKey))
                .catch(err => reject(err));
        }),
    fetchValueOptionsPaginated: (variables) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: PropertyValueOptionsPaginatedGql,
                    variables,
                    fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.CO_PropertyValueOptionsPaginated))
                .catch(err => reject(err));
        }),
    moveValueOption: (id, reference, placement = 'before') =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: MovePropertyValueOptionGql,
                    variables: { id: parseInt(id), reference: parseInt(reference), placement }
                })
                .then(({ data }) => resolve(data.CO_MovePropertyValueOption))
                .catch(err => reject(err));
        }),
    restoreValueOptionOrder: (id) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: RestorePropertyValueOptionOrderGql,
                    variables: { id: parseInt(id) }
                })
                .then(({ data }) => resolve(data.CO_RestorePropertyValueOptionOrder))
                .catch(err => reject(err));
        }),
    sortValueOptions: (id, order = 'natural') =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: SortPropertyValueOptionsGql,
                    variables: { id: parseInt(id), order }
                })
                .then(({ data }) => resolve(data.CO_SortPropertyValueOptions))
                .catch(err => reject(err));
        }),
    mergeValueOptions: (survivor, duplicates) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: MergePropertyValueOptionsGql,
                    variables: { survivor: parseInt(survivor), duplicates: duplicates.map(id => parseInt(id)) }
                })
                .then(({ data }) => resolve(data.CO_MergePropertyValueOptions))
                .catch(err => reject(err));
        }),
    fetchKeys: (shopID = null, articleRequired = null) =>
        new Promise((resolve, reject) => {
            let variables = {};
            if (shopID) variables.shop = parseInt(shopID);
            if (articleRequired) variables.article_required = articleRequired;

            apolloClient
                .query({
                    query: PropertyKeysGql,
                    variables
                })
                .then(({ data }) => resolve(data.CO_Properties))
                .catch(err => reject(err));
        }),
    fetchList: (shopID = null) =>
        new Promise((resolve, reject) => {
            let variables = {};
            if (shopID) variables.shop = parseInt(shopID);

            apolloClient
                .query({
                    query: PropertiesGql,
                    variables
                })
                .then(({ data }) => resolve(data.CO_Properties))
                .catch(err => reject(err));
        }),
    fetchPropertiesPaginated: (data, withArticles = false) =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            let query = PropertiesPaginatedGql
            if (withArticles) query = PropertiesWithArticlesPaginatedGql

            apolloClient
                .query({
                    query,
                    variables: {
                        scope: _.get(data, 'scope', 'ex'),
                        type: _.get(data, 'type', 'shop_property'),
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        filters,
                    }
                })
                .then(({ data }) => resolve(data.CO_PropertiesPaginated))
                .catch(err => reject(err));
        }),
    fetchProperty: (id, shopID = null, withValueOptions = true) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            let variables = { id: parseInt(id), withValueOptions };
            if (shopID) variables.shop = parseInt(shopID);

            apolloClient
                .query({
                    query: PropertyGql,
                    variables,
                    fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.CO_Property))
                .catch(err => reject(err));
        });
    },
    fetchTranslation: id =>
        new Promise((resolve, reject) => {
            let variables = { translation: true };
            if (id) variables.id = parseInt(id);

            apolloClient
                .query({
                    query: PropertyGql,
                    variables,
                    fetchPolicy: 'no-cache'
                })
                .then(({ data }) => resolve(data.CO_Property))
                .catch(err => reject(err));
        }),
    translate: (property, language, properties = [], type = 'create-empty') => {
        if (property === undefined) throw new Error('required argument is missing');
        if (language === undefined) throw new Error('required argument is missing');
        if (type === undefined) throw new Error('required argument is missing');

        property = _.removeDeepByKey(property, '__typename');
        language = _.removeDeepByKey(language, '__typename');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: TranslatePropertyGql,
                    variables: { property, language, properties, type }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_TranslateProperty);
                })
                .catch(err => reject(err));
        });
    },
    create: (property, translation = false) => {
        if (property === undefined) throw new Error('required argument is missing');

        property = reduceConditionFields(_.removeDeepByKey(property, '__typename'));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: CreatePropertyGql,
                    variables: { property, translation }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_CreateProperty);
                })
                .catch(err => reject(err));
        });
    },
    update: (property, translation = false, withValueOptions = true) => {
        if (property === undefined) throw new Error('required argument is missing');

        property = reduceConditionFields(_.removeDeepByKey(property, '__typename'));
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: UpdatePropertyGql,
                    variables: { property, translation, withValueOptions }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_UpdateProperty);
                })
                .catch(err => reject(err));
        });
    },
    delete: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: DeletePropertyGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_DeleteProperty);
                })
                .catch(err => reject(err));
        });
    },

    clone: id => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ClonePropertyGql,
                    variables: { propertyId: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_CloneProperty);
                })
                .catch(err => reject(err));
        });
    },

    clearUnlinkedValueOptions: (id) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ClearUnlinkedPropertyValueOptionsGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_ClearUnlinkedPropertyValueOptions);
                })
                .catch(err => reject(err));
        });
    },

    exportValueOptions: (id) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ExportPropertyValueOptionsGql,
                    variables: { id: parseInt(id) }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_ExportPropertyValueOptions);
                })
                .catch(err => reject(err));
        });
    },

    validateValueOptionImportCsv: (id, file) => {
        if (id === undefined) throw new Error('required argument is missing');

        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ValidatePropertyValueOptionImportCsvGql,
                    variables: { id: parseInt(id), file }
                })
                .then(({ data }) => resolve(data.CO_ValidatePropertyValueOptionImportCsv))
                .catch(err => reject(err));
        });
    },

    importValueOptions: (id, file) => {
        if (id === undefined) throw new Error('required argument is missing');

        file = _.removeDeepByKey(file, '__typename');
        return new Promise((resolve, reject) => {
            apolloClient
                .mutate({
                    mutation: ImportPropertyValueOptionsGql,
                    variables: { id: parseInt(id), file }
                })
                .then(async ({ data }) => {
                    await apolloClient.clearStore();
                    resolve(data.CO_ImportPropertyValueOptions);
                })
                .catch(err => reject(err));
        });
    },

    processPropertyPaginationBulkAction: (action, data) => {
        return new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .mutate({
                    mutation: ProcessPropertyPaginationBulkActionGql,
                    variables: {
                        action,
                        filters,
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                    },
                })
                .then(({ data }) => resolve(data.CO_ProcessPropertyPaginationBulkAction))
                .catch(err => reject(err));
        });
    },
};
